import {
    AlertType,
    Toast,
    ToastContainer as ToastContainerUI,
} from '@/shared/components/ui/Toast.tsx';
import {TOAST_CONFIG} from '@/shared/constants.ts';
import {generateId} from '@/shared/utils/text.ts';
import {createContext, ReactNode, useCallback, useContext, useMemo, useState} from 'react';

type ToastContextType = {
    toasts: Toast[];
    showToast: (message: string, type?: AlertType, duration?: number) => void;
    removeToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({children}: {children: ReactNode}) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const showToast = useCallback(
        (
            message: string,
            type: AlertType = 'info',
            duration: number = TOAST_CONFIG.DEFAULT_DURATION_MS
        ) => {
            const id = generateId('toast');

            setToasts((prev) => [...prev, {id, message, type}]);

            // Auto-remove after duration
            setTimeout(() => {
                removeToast(id);
            }, duration);

            // Haptic feedback on mobile
            if ('vibrate' in navigator) {
                navigator.vibrate(TOAST_CONFIG.HAPTIC_DURATION_MS);
            }
        },
        [removeToast]
    );

    const value = useMemo(
        () => ({toasts, showToast, removeToast}),
        [toasts, showToast, removeToast]
    );

    return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

/**
 * Toast container component that connects to ToastContext.
 * Should be placed at the root of your app.
 */
export const ToastContainer = () => {
    const {toasts, removeToast} = useToast();
    return <ToastContainerUI toasts={toasts} onRemove={removeToast} />;
};
